// Short "money" screen between pages: a green wipe with a spinning coin and bills floating up.
// Runs on every page change (not the first load, not inside the admin area, not for people who asked for
// less motion). It never blocks clicks and is gone in under a second.
import { useEffect, useRef, useState } from "react";
import { useLocation } from "react-router-dom";

const MS = 900;

const CSS = `
@keyframes g2c-wipe { 0%{opacity:0;transform:translateY(0)} 12%{opacity:1;transform:translateY(0)} 62%{opacity:1;transform:translateY(0)} 100%{opacity:1;transform:translateY(-102%)} }
@keyframes g2c-coin { 0%{transform:rotateY(0) scale(.6);opacity:0} 20%{opacity:1;transform:rotateY(180deg) scale(1)} 100%{transform:rotateY(1080deg) scale(1)} }
@keyframes g2c-bill { 0%{transform:translateY(40px) rotate(var(--r)) scale(.6);opacity:0} 25%{opacity:1} 100%{transform:translateY(-140px) rotate(calc(var(--r) * -1)) scale(1);opacity:0} }
@keyframes g2c-text { 0%,15%{opacity:0;transform:translateY(8px)} 35%,60%{opacity:1;transform:translateY(0)} 100%{opacity:0} }
`;

function Bill({ left, delay, rot, size }) {
    return (
        <svg viewBox="0 0 80 40" width={size} height={size / 2} aria-hidden="true"
            style={{ position: "absolute", left, top: "58%", "--r": `${rot}deg`, animation: `g2c-bill ${MS * 0.9}ms ease-out ${delay}ms both` }}>
            <rect x="1" y="1" width="78" height="38" rx="5" fill="#86efac" stroke="#14532d" strokeWidth="2" />
            <rect x="6" y="6" width="68" height="28" rx="3" fill="none" stroke="#166534" strokeWidth="1" strokeDasharray="3 2" />
            <circle cx="40" cy="20" r="10" fill="#22c55e" stroke="#14532d" strokeWidth="1.5" />
            <text x="40" y="25" textAnchor="middle" fontSize="14" fontWeight="800" fill="#14532d" fontFamily="system-ui, sans-serif">$</text>
        </svg>
    );
}

export default function MoneyTransition() {
    const { pathname } = useLocation();
    const prev = useRef(pathname);
    const [run, setRun] = useState(0);

    useEffect(() => {
        const from = prev.current;
        prev.current = pathname;
        if (from === pathname) return undefined;
        try {
            if (window.matchMedia("(prefers-reduced-motion: reduce)").matches) return undefined;
        } catch { /* ignore */ }
        if (from.startsWith("/admin") && pathname.startsWith("/admin")) return undefined;
        setRun((n) => n + 1);
        const t = setTimeout(() => setRun(0), MS + 80);
        return () => clearTimeout(t);
    }, [pathname]);

    if (!run) return null;
    return (
        <div key={run} aria-hidden="true" className="fixed inset-0 z-[9999] pointer-events-none overflow-hidden"
            style={{ background: "linear-gradient(160deg,#065f46 0%,#047857 55%,#0f766e 100%)", animation: `g2c-wipe ${MS}ms cubic-bezier(.77,0,.18,1) both` }}>
            <style>{CSS}</style>
            <div className="absolute inset-0 opacity-20" style={{ backgroundImage: "radial-gradient(circle at 20% 20%, #fbbf24 0, transparent 35%), radial-gradient(circle at 80% 80%, #34d399 0, transparent 40%)" }} />
            {[["18%", 40, -14, 64], ["32%", 120, 10, 52], ["46%", 0, -6, 76], ["60%", 160, 16, 56], ["74%", 80, -12, 66]].map(([l, d, r, s]) => (
                <Bill key={l} left={l} delay={d} rot={r} size={s} />
            ))}
            <div className="absolute inset-0 flex flex-col items-center justify-center" style={{ perspective: 600 }}>
                <svg viewBox="0 0 100 100" width="92" height="92" style={{ animation: `g2c-coin ${MS}ms ease-in-out both`, filter: "drop-shadow(0 8px 14px rgba(0,0,0,.35))" }}>
                    <circle cx="50" cy="50" r="46" fill="#fbbf24" stroke="#b45309" strokeWidth="5" />
                    <circle cx="50" cy="50" r="35" fill="none" stroke="#b45309" strokeWidth="3" />
                    <text x="50" y="66" textAnchor="middle" fontSize="48" fontWeight="800" fill="#92400e" fontFamily="system-ui, sans-serif">$</text>
                </svg>
                <div className="mt-5 text-white/95 font-bold tracking-wide text-sm" style={{ animation: `g2c-text ${MS}ms ease both` }}>GiftToCash</div>
            </div>
        </div>
    );
}
